/**
 * Standalone app.
 *
 * - `/` is the public landing page.
 * - `/moment/*` is the panel: session + shell around the embeddable `MomentModule`.
 *   Dev build: every request carries `X-Org-Id` (accepted by the API when ENV=dev).
 *   Production build: cookie session only. The API sets the HttpOnly cookie via
 *   `GET /api/v1/auth/session?token=<jwt>` (see docs/DEPLOYMENT.md); the shell asks `/auth/me` and shows the
 *   sign-in screen when there is no session.
 */
import { useEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { MomentProvider } from '@/module/MomentProvider';
import { MomentModule } from '@/module/MomentModule';
import { AppShell } from '@/app/AppShell';
import { SignIn, type Me } from '@/app/SignIn';
import { LandingPage } from '@/landing/LandingPage';
import { ApiClient } from '@/api/client';
import { useLocalStorage } from '@/lib/hooks';

const BASE = '/moment';
const DEV_ORG = '1e9b6bdc-264d-4996-b1cd-ff811016f928'; // seeded demo org (scripts/seed.py)
const DEV = import.meta.env.DEV;

type SessionState = { kind: 'loading' } | { kind: 'anonymous' } | { kind: 'ready'; me: Me };

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path={`${BASE}/*`} element={<PanelApp />} />
        <Route path="*" element={<Navigate to={`${BASE}/videos`} replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function PanelApp() {
  const [devOrg] = useLocalStorage<string>('moment_dev_org', DEV_ORG);
  const [session, setSession] = useState<SessionState>(
    DEV ? { kind: 'ready', me: { org_id: devOrg, user_id: 'dev', is_admin: true } } : { kind: 'loading' },
  );

  useEffect(() => {
    if (DEV) return;
    let cancelled = false;
    new ApiClient({ baseUrl: '/api/v1' })
      .request<Me>('GET', '/auth/me')
      .then((me) => !cancelled && setSession({ kind: 'ready', me }))
      .catch(() => !cancelled && setSession({ kind: 'anonymous' }));
    return () => {
      cancelled = true;
    };
  }, []);

  const config = useMemo(
    () =>
      DEV
        ? { baseUrl: '/api/v1', orgId: devOrg, getAuthHeaders: () => ({ 'X-Org-Id': devOrg }) }
        : { baseUrl: '/api/v1' },
    [devOrg],
  );

  if (session.kind === 'loading') return null;
  if (session.kind === 'anonymous') return <SignIn onSignedIn={(me) => setSession({ kind: 'ready', me })} />;

  const me = session.me;
  const user = {
    name: me.user_id ?? 'Session',
    email: `org ${me.org_id.slice(0, 8)}`,
    plan: me.is_admin ? 'Admin' : undefined,
  };
  const signOut = DEV
    ? undefined
    : () =>
        new ApiClient({ baseUrl: '/api/v1' })
          .request('POST', '/auth/logout')
          .finally(() => setSession({ kind: 'anonymous' }));

  return (
    <MomentProvider config={config}>
      <AppShell user={user} onSignOut={signOut}>
        <Routes>
          <Route path="/*" element={<MomentModule basePath={BASE} />} />
        </Routes>
      </AppShell>
    </MomentProvider>
  );
}
